import React, { useState, useEffect } from 'react';
import { 
  ClipboardCheck, Globe, GraduationCap, Book, BookOpen,
  Smartphone, Headphones, FileText, Mic, Mail, Linkedin,
  ExternalLink, ArrowRight, Link2, Twitter, Instagram, Youtube,
  CheckCircle, Loader2, UserPlus
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Inline subscribe form (used in BioLinks) ── */
const BioSubscribe = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/cfq/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, first_name: firstName, source: 'bio-links' }),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex items-center gap-3 w-full bg-white/10 border border-gold/40 rounded-xl px-5 py-4" data-testid="bio-subscribe-success">
        <CheckCircle size={18} className="text-emerald-400 flex-shrink-0" />
        <span className="text-white text-sm">Check your inbox to confirm.</span>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="group flex items-center gap-3 w-full bg-gold/20 hover:bg-gold/30 border border-gold/40 hover:border-gold/60 rounded-xl px-5 py-4 transition-all duration-200 shadow-lg"
        data-testid="bio-subscribe-btn"
      >
        <div className="w-10 h-10 bg-gold/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gold/30 transition-colors">
          <Mail size={20} className="text-gold" />
        </div>
        <span className="font-medium text-white group-hover:text-gold transition-colors flex-1 text-left">
          Join the Cultural Fluency List
        </span>
        <ArrowRight size={18} className="text-slate-500 group-hover:text-gold group-hover:translate-x-1 transition-all flex-shrink-0" />
      </button>
    );
  }

  return (
    <div className="w-full bg-gold/20 border border-gold/40 rounded-xl px-5 py-4" data-testid="bio-subscribe-form">
      <div className="flex items-center gap-2 mb-3">
        <Mail size={16} className="text-gold" />
        <span className="text-white text-sm font-medium">Join the Cultural Fluency List</span>
      </div>
      <form onSubmit={handleSubmit} className="space-y-2">
        <input
          type="text"
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          placeholder="Your first name"
          className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/50"
        />
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Your email address"
          required
          className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/50"
        />
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!email || status === 'loading'}
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40"
            style={{ backgroundColor: '#C99316', color: '#071B3A' }}
          >
            {status === 'loading' ? <Loader2 size={14} className="animate-spin" /> : 'Subscribe'}
          </button>
          <button type="button" onClick={() => setOpen(false)} className="px-3 py-2 text-white/40 hover:text-white/70 text-xs transition-colors">
            Cancel
          </button>
        </div>
        {status === 'error' && <p className="text-red-400 text-xs">Something went wrong — please try again.</p>}
      </form>
    </div>
  );
};

const ICON_MAP = {
  'clipboard-check': ClipboardCheck,
  'globe': Globe,
  'graduation-cap': GraduationCap,
  'book': Book,
  'book-open': BookOpen,
  'smartphone': Smartphone,
  'headphones': Headphones,
  'file-text': FileText,
  'mic': Mic,
  'mail': Mail,
  'linkedin': Linkedin,
  'twitter': Twitter,
  'instagram': Instagram,
  'youtube': Youtube,
  'link': Link2,
  'user-plus': UserPlus,
};

const BioLinks = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/public/bio-profile`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setProfile(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-navy flex items-center justify-center"><div className="w-6 h-6 border-2 border-gold/30 border-t-gold rounded-full animate-spin" /></div>;

  const links = (profile?.links || []).sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy via-navy to-slate-900" data-testid="bio-links-page">
      <div className="max-w-[720px] mx-auto px-6 py-12 md:py-16">
        
        {/* Author Image */}
        <div className="text-center mb-10">
          {profile?.profile_image && (
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute -inset-1 bg-gold/30 rounded-full blur-sm" />
                <img
                  src={profile.profile_image}
                  alt={profile?.name || 'Profile'}
                  className="relative w-28 h-28 md:w-32 md:h-32 rounded-full object-cover object-top border-2 border-gold/50 shadow-xl"
                  data-testid="bio-profile-image"
                />
              </div>
            </div>
          )}
          
          {profile?.logo_image && (
            <div className="flex justify-center mb-4">
              <img src={profile.logo_image} alt="Logo" className="h-10 md:h-12 w-auto" />
            </div>
          )}
          
          {profile?.tagline && (
            <p
              className="text-cream/90 text-lg md:text-xl mb-3 text-center"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, 'Times New Roman', serif", fontStyle: 'italic', letterSpacing: '0.01em' }}
            >
              {profile.tagline}
            </p>
          )}
          
          <div className="w-16 h-0.5 bg-gold mx-auto mb-4" />
          
          {profile?.subtitle && (
            <p className="text-slate-400 text-sm md:text-base">{profile.subtitle}</p>
          )}
        </div>

        {/* Links */}
        <div className="space-y-3 md:space-y-4">
          {/* Subscribe — always first */}
          <BioSubscribe />

          {links.map((link) => {
            const Icon = ICON_MAP[link.icon] || Link2;
            const ButtonContent = (
              <>
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors">
                    <Icon size={20} className="text-gold" />
                  </div>
                  <span className="font-medium text-white group-hover:text-gold transition-colors text-left">
                    {link.title}
                  </span>
                </div>
                {link.is_external ? (
                  <ExternalLink size={18} className="text-slate-500 group-hover:text-gold transition-colors flex-shrink-0" />
                ) : (
                  <ArrowRight size={18} className="text-slate-500 group-hover:text-gold group-hover:translate-x-1 transition-all flex-shrink-0" />
                )}
              </>
            );

            if (link.is_external || link.url?.startsWith('mailto:')) {
              return (
                <a
                  key={link.id}
                  href={link.url}
                  target={link.url?.startsWith('mailto:') ? '_self' : '_blank'}
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 w-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold/30 rounded-xl px-5 py-4 transition-all duration-200 shadow-lg hover:shadow-xl"
                  data-testid={`bio-link-${link.id}`}
                >
                  {ButtonContent}
                </a>
              );
            }

            return (
              <Link
                key={link.id}
                to={link.url}
                className={`group flex items-center gap-3 w-full rounded-xl px-5 py-4 transition-all duration-200 shadow-lg hover:shadow-xl ${
                  link.highlight 
                    ? 'bg-gold/20 hover:bg-gold/30 border border-gold/40 hover:border-gold/60' 
                    : 'bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold/30'
                }`}
                data-testid={`bio-link-${link.id}`}
              >
                {ButtonContent}
              </Link>
            );
          })}
        </div>

        {/* Testimonials */}
        {profile?.testimonials?.length > 0 && (
          <div className="mt-10">
            <h3 className="text-white/80 text-xs font-semibold uppercase tracking-wider mb-4 text-center">
              What People Are Saying
            </h3>
            <div className="space-y-3">
              {profile.testimonials.map((t, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-white/90 text-sm italic mb-2">"{t.quote}"</p>
                  <p className="text-gold text-xs font-medium">— {t.author}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Visit Full Site */}
        <div className="mt-10 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-gold hover:text-white transition-colors text-sm font-medium">
            <span>Visit Full Website</span><ArrowRight size={16} />
          </Link>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-white/10">
          <div className="text-center space-y-4">
            <p className="text-slate-500 text-sm">&copy; {profile?.name || 'Promise Monday'}. All rights reserved.</p>
            <div className="flex justify-center gap-4 text-sm">
              <Link to="/privacy" className="text-slate-500 hover:text-gold transition-colors">Privacy Policy</Link>
              <span className="text-slate-700">&bull;</span>
              <Link to="/terms" className="text-slate-500 hover:text-gold transition-colors">Terms of Use</Link>
            </div>
            <p className="text-slate-600 text-xs max-w-md mx-auto">No tracking cookies on this page unless you consent.</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default BioLinks;
